// Pure helpers for the tutoring interest form (JED-1486). The wire values match the backend's
// TutoringInterest record (feedbacksBackend domain/tutoring).
import { resolveProfileStates } from '../../../utils/function';

export const CONSENT_TEXT =
  'I agree that Jeddle can use these details to contact me about tutoring.';

export const PRIVACY_URL = 'https://jeddle.com/privacy-policy/';

export const YEARS = [
  'Year 7',
  'Year 8',
  'Year 9',
  'Year 10',
  'Year 11',
  'Year 12',
];
export const STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];
export const OUTSIDE_AUSTRALIA = 'Outside Australia';
export const SUBJECTS = [
  'English',
  'Maths',
  'Sciences',
  'Humanities',
  'Languages',
  'Other',
];

export const FORMATS = [
  {
    value: 'ONLINE',
    label: 'Online',
    hint: 'Video sessions from home',
    summary: 'Online',
  },
  {
    value: 'IN_PERSON',
    label: 'In person',
    hint: 'Face to face, near you',
    summary: 'In person',
  },
  {
    value: 'EITHER',
    label: 'Either works',
    hint: 'Whatever fits best',
    summary: 'Online or in person',
  },
];

export const GROUP_SIZES = [
  {
    value: 'ONE_TO_ONE',
    label: '1:1',
    hint: 'Just you and your tutor',
    summary: '1:1',
  },
  {
    value: 'SMALL_GROUP',
    label: 'Small group',
    hint: 'Learn with a few other students',
    summary: 'Small group',
  },
  {
    value: 'NOT_SURE',
    label: 'Not sure yet',
    hint: 'We can talk it through',
    summary: '1:1 or group',
  },
];

export const START_TIMINGS = [
  { value: 'THIS_WEEK', label: 'This week' },
  { value: 'BEFORE_EXAMS', label: 'Before my exams' },
  { value: 'NEXT_TERM', label: 'Next term' },
  { value: 'JUST_EXPLORING', label: 'Just exploring' },
];

// Same limits as the backend record, so a long paste is cut here rather than refused there.
export const LIMITS = {
  name: 100,
  email: 254,
  phone: 30,
  parentName: 100,
  parentContact: 254,
  suburb: 100,
  notes: 2000,
};

export const ERRORS = {
  name: 'Add your name.',
  email: 'Add an email we can reach you on.',
  phone: 'Add a mobile number we can call or text.',
  format: 'Choose how you’d like to meet.',
  groupSize: 'Choose 1:1, small group or not sure.',
  consent: 'Tick the box so we’re allowed to contact you.',
};

// Field order for focusing the first error.
export const FIELD_ORDER = [
  'name',
  'email',
  'phone',
  'format',
  'groupSize',
  'consent',
];

// The backend checks the address with Hibernate's @Email. Refuse here what it refuses (dots at either end of
// the local part or doubled, a local part over 64 characters), and also an address with no dot in its
// domain, so a typo is caught on the form instead of coming back as a 400.
const LOCAL_PART =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/;
const DOMAIN_LABEL = /^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/;

// A phone number as the backend accepts it: 8 to 15 digits, optionally separated by spaces, dashes, dots,
// brackets or a +.
// Any space (a pasted non-breaking one too) becomes a plain space first, because the backend only takes
// a plain one.
const PHONE = /^[ +().-]*(?:\d[ +().-]*){8,15}$/;

export const phoneText = (value) => text(value).replace(/\s+/g, ' ');

export const isValidPhone = (value) => {
  const phone = phoneText(value);
  return phone.length <= LIMITS.phone && PHONE.test(phone);
};

export const isValidEmail = (value) => {
  const email = text(value);
  const at = email.lastIndexOf('@');
  if (at < 1 || email.length > LIMITS.email) return false;
  const local = email.slice(0, at);
  const labels = email.slice(at + 1).split('.');
  return (
    local.length <= 64 &&
    LOCAL_PART.test(local) &&
    labels.length > 1 &&
    labels.every((label) => label.length <= 63 && DOMAIN_LABEL.test(label))
  );
};

// The app's surfaces, from the popup's product-event trigger. Every paywall that is not the header,
// onboarding or the phone sheet is a credit or feature wall.
const SOURCES_BY_TRIGGER = {
  credits_pill: 'HEADER',
  onboarding_plans: 'ONBOARDING',
  mobile: 'PHONE',
};

export const sourceFor = (eventTrigger) =>
  SOURCES_BY_TRIGGER[eventTrigger] || 'CREDIT_WALL';

// Triggers that open the plans over a draft (credit and mark walls), so the way out is "Back to my draft".
const DRAFT_TRIGGERS = [
  'credit_shortfall',
  'question_limit',
  'view_mark',
  'submit_gate',
];

export const backLabel = ({ trigger, fromDraft = false }) =>
  fromDraft || DRAFT_TRIGGERS.includes(trigger)
    ? 'Back to my draft'
    : 'Back to JeddAI';

// What a refused submit says. `field` is the form field the backend named in its 400, when it did.
export const FAILURES = {
  invalid: 'Check your details and try again.',
  forbidden:
    'Tutoring interest is for student accounts. Sign in with your student account to register.',
  failed: 'We couldn’t save your details. Check your connection and try again.',
};

export const failureMessage = (result) => {
  if (result?.error === 'invalid' && ERRORS[result.field]) {
    return `Check your details: ${ERRORS[result.field]}`;
  }
  return FAILURES[result?.error] || FAILURES.failed;
};

// Product-event value for the same source.
export const eventSource = (source) =>
  String(source || 'CREDIT_WALL').toLowerCase();

const text = (value) => (typeof value === 'string' ? value.trim() : '');

// "12", "Year 12" and "year 12" all mean Year 12; anything else is kept as typed so it is not lost.
export const yearFromProfile = (year) => {
  const value = text(year == null ? '' : String(year));
  if (!value) return '';
  const number = value.match(/^(?:year\s*)?(\d{1,2})$/i);
  return number ? `Year ${Number(number[1])}` : value;
};

export const stateFromProfile = (profile) => {
  const first = text((resolveProfileStates(profile) || [])[0]).toUpperCase();
  if (STATES.includes(first)) return first;
  const country = text(profile?.country).toUpperCase();
  return country && !['AU', 'AUS', 'AUSTRALIA'].includes(country)
    ? OUTSIDE_AUSTRALIA
    : '';
};

// `prefill` is the backend's { name, email } from the WordPress account: the app profile has a name but
// no email. The account email wins over nothing; the profile's name wins over the account's.
export const initialValues = (profile, prefill = null) => ({
  name: (text(profile?.name) || text(prefill?.name)).slice(0, LIMITS.name),
  email: (text(prefill?.email) || text(profile?.email)).slice(0, LIMITS.email),
  phone: '',
  year: yearFromProfile(profile?.year),
  state: stateFromProfile(profile),
  addParent: false,
  parentName: '',
  parentContact: '',
  subjects: ['English'],
  format: null,
  suburb: '',
  groupSize: null,
  startTiming: null,
  notes: '',
  consent: false,
});

// A prefill that arrives after the form opened fills only what is still empty, never what they typed.
export const applyPrefill = (values, prefill) => {
  if (!prefill) return values;
  return {
    ...values,
    name: text(values.name)
      ? values.name
      : text(prefill.name).slice(0, LIMITS.name),
    email: text(values.email)
      ? values.email
      : text(prefill.email).slice(0, LIMITS.email),
  };
};

export const needsSuburb = (format) =>
  format === 'IN_PERSON' || format === 'EITHER';

export const toggleSubject = (subjects, subject) =>
  subjects.includes(subject)
    ? subjects.filter((s) => s !== subject)
    : [...subjects, subject];

export const validate = (values) => {
  const errors = {};
  if (!text(values.name)) errors.name = ERRORS.name;
  if (!isValidEmail(values.email)) errors.email = ERRORS.email;
  if (!isValidPhone(values.phone)) errors.phone = ERRORS.phone;
  if (!values.format) errors.format = ERRORS.format;
  if (!values.groupSize) errors.groupSize = ERRORS.groupSize;
  if (values.consent !== true) errors.consent = ERRORS.consent;
  return errors;
};

const optional = (value) => text(value) || null;

// The body for POST /users/tutoring-interest. Hidden answers (the parent block when unticked, the
// suburb for Online) are not sent.
export const interestPayload = (values, { source, trigger }) => ({
  name: text(values.name),
  email: text(values.email),
  phone: phoneText(values.phone),
  year: optional(values.year),
  state: optional(values.state),
  parent: values.addParent
    ? {
        name: optional(values.parentName),
        contact: optional(values.parentContact),
      }
    : null,
  subjects: values.subjects,
  format: values.format,
  suburb: needsSuburb(values.format) ? optional(values.suburb) : null,
  groupSize: values.groupSize,
  startTiming: values.startTiming,
  notes: optional(values.notes),
  consent: values.consent === true,
  source,
  trigger: /^[a-z_]{1,40}$/.test(trigger || '') ? trigger : null,
});

const labelOf = (options, value) =>
  options.find((option) => option.value === value)?.summary ||
  options.find((option) => option.value === value)?.label;

// The chips on the thank-you screen: "Online", "1:1", "English, Maths", "Before my exams".
export const summaryChips = (values) =>
  [
    labelOf(FORMATS, values.format),
    labelOf(GROUP_SIZES, values.groupSize),
    values.subjects.length ? values.subjects.join(', ') : null,
    labelOf(START_TIMINGS, values.startTiming),
  ].filter(Boolean);

export const firstName = (name) => text(name).split(/\s+/)[0] || 'there';
